// chainFieldMarkup.ts
import { CHAIN_FIELDS, isChainField } from './measurementChain';
import { splitValueAndUnit } from './measurementValueParts';

/**
 * Give a measurement value an identity in the report body.
 *
 * The measurement table is built by substituting `{{LVIDd}}`-style placeholders
 * with plain text (`populateTemplate` in ReportsLayout). Plain text has no
 * identity, which is exactly why nothing could ever be kept in sync: there is
 * no way to find "the LVIDd value" in the document afterwards.
 *
 * The mechanism is deliberately the SAME one the numeric stress slots already
 * use (`wrapNumericField` in numericReportFields.ts): a span carrying the key
 * as its first class, backed by a Mark in `reportExtension.ts`, read back with
 * `classList[0]`. Nothing new is invented — chaining just needs the identity
 * that pattern already provides.
 *
 * DELIBERATELY CONSERVATIVE — the mapping is in production and works:
 * - Only keys in `CHAIN_FIELDS` are wrapped. Every other placeholder keeps
 *   emitting exactly the plain text it emits today.
 * - An empty/missing value emits `''`, exactly as today. Wrapping a blank
 *   would put an empty clickable span where the report currently shows an
 *   empty cell, and an empty span collapses to nothing anyway.
 * - Existing saved reports are untouched: this runs only when a report is
 *   first built from a raw template string.
 */
export function wrapChainField(key: string, value: string): string {
  if (!isChainField(key)) {
    return value;
  }
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) {
    return value;
  }
  const label = CHAIN_FIELDS[key].label;

  // EF is printed "69 %" — wrap the figure only, so the "%" sits outside the mark and
  // typing after it is never absorbed into this occurrence. See chainFieldBackfill.ts,
  // which marks the same boundary on an already-saved report.
  if (CHAIN_FIELDS[key].unit === '%') {
    const { number } = splitValueAndUnit(text);
    if (number && text.startsWith(number)) {
      const rest = text.slice(number.length);
      return (
        `<span data-chain-field class="${key}" title="${escapeAttr(label)}">` +
        `${escapeText(number)}</span>${escapeText(rest)}`
      );
    }
  }

  return (
    `<span data-chain-field class="${key}" title="${escapeAttr(label)}">` +
    `${escapeText(text)}</span>`
  );
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
